import React, { useState } from 'react';
import dayjs from 'dayjs';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { Alert, Box, Button, Chip, CircularProgress, Divider, Link, Skeleton, Tooltip, Typography } from '@mui/material';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DoNotDisturbOnOutlinedIcon from '@mui/icons-material/DoNotDisturbOnOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhoneDisabledOutlinedIcon from '@mui/icons-material/PhoneDisabledOutlined';
import PhoneForwardedOutlinedIcon from '@mui/icons-material/PhoneForwardedOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';

import { useToast } from '../../../common/hooks/useToast';
import { Item } from '../../../styles/common';
import { BatchHistory } from '../batch/BatchHistory';
import { BatchUpdateDialog } from '../batch/BatchUpdateDialog';
import { forgetBatchData } from '../batch/batchUpdate';
import { CrmInvalidId, CrmPageFrame } from '../CrmPageFrame';
import { contactPath, CRM_PATHS, crmReturnState, emailListPath, listPeoplePath, parseId } from '../crmPaths';
import { describeListError, EMPTY_STATE } from '../contacts/contactList';
import { queuePathFor } from '../callQueue/callQueue';
import { count, describeManageError, formatDay, sinceText } from '../manage/manage';
import { StatTile, StatTiles } from '../manage/ManageParts';
import { DeleteListDialog } from './DeleteListDialog';
import { ListEditorDialog } from './ListEditorDialog';
import { kindLabel, madeAt, ManagedList, originLabel } from './listManager';
import { CRM_LIST_DETAIL, CRM_LIST_PEOPLE, CRM_RECOUNT_LIST } from './lists.queries';

interface Counts {
  total: number;
  withEmail: number;
  withPhone: number;
  canEmail: number;
  canCall: number;
  canText: number;
  doNotCall: number;
  doNotContact: number;
}

interface Person {
  id: number;
  fullName: string | null;
  primaryEmail: string | null;
  primaryPhone: string | null;
  city: string | null;
  countryCode: string | null;
}

/** The tiles over a list's people: each opens them on the contact list, that gate filtered. */
const TILES: Array<{
  key: keyof Counts;
  label: string;
  param?: string;
  hint?: (c: Counts) => string;
  icon: React.ReactNode;
  tone: string;
}> = [
  { key: 'total', label: 'People', hint: () => 'Live records', icon: <GroupOutlinedIcon />, tone: '#1565c0' },
  { key: 'canEmail', label: 'Can email', param: 'canEmail', hint: (c) => `${count(c.withEmail)} have an address`, icon: <MailOutlineIcon />, tone: '#2e7d32' },
  { key: 'canCall', label: 'Can call', param: 'canCall', hint: (c) => `${count(c.withPhone)} have a number`, icon: <CallOutlinedIcon />, tone: '#2e7d32' },
  { key: 'canText', label: 'Can text', param: 'canText', icon: <SmsOutlinedIcon />, tone: '#2e7d32' },
  { key: 'doNotCall', label: 'Do not call', param: 'doNotCall', icon: <PhoneDisabledOutlinedIcon />, tone: '#c62828' },
  { key: 'doNotContact', label: 'Do not contact', param: 'doNotContact', icon: <DoNotDisturbOnOutlinedIcon />, tone: '#c62828' },
];

const Fact = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" component="p">
      {label}
    </Typography>
    <Typography variant="body2" component="div">
      {children}
    </Typography>
  </Box>
);

/**
 * One list or saved filter (card 20): who is on it now, with their consent, the
 * first of them, and every batch update run on it. NationBuilder's list page,
 * where a batch update starts.
 *
 * - A **list** is its members. Batch update reaches all of them; "Open on the
 *   contact list" shows them there, to page, sort, filter further or tick a few.
 * - A **saved filter** is whoever its filter matches now. It can be batch
 *   updated the same way. One migrated from NationBuilder and never
 *   re-expressed has no filter, and so no people: the page says so.
 *
 * Requests on opening: the list (`crmList`), then its counts and first people
 * (two root fields), and its batch history. Nothing polls.
 */
export const CrmListDetail = () => {
  const { listId: param } = useParams();
  const id = parseId(param);
  const navigate = useNavigate();
  const location = useLocation();
  const client = useApolloClient();
  const toast = useToast();
  const [batching, setBatching] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [today] = useState(() => dayjs().format('YYYY-MM-DD'));

  const detail = useQuery<{ crmList: ManagedList & { filter: Record<string, unknown> | null } }>(CRM_LIST_DETAIL, {
    variables: { id },
    skip: id === null,
    fetchPolicy: 'cache-and-network',
  });
  const list = detail.data?.crmList;
  const selectable = Boolean(list && (list.type === 'STATIC' || (list.isActive && list.filter)));
  const filter = { segments: { anyOf: [id as number] } };

  const peopleQuery = useQuery<{ counts: Counts; people: { items: Array<Person> } }>(CRM_LIST_PEOPLE, {
    variables: { filter },
    skip: !selectable,
    fetchPolicy: 'cache-and-network',
  });
  const counts = peopleQuery.data?.counts;
  const firstPeople = peopleQuery.data?.people.items;

  const [recount, recounting] = useMutation<{ crmRecountList: ManagedList }>(CRM_RECOUNT_LIST);

  if (id === null) {
    return <CrmInvalidId param={param} noun="list" listLabel="Lists" listPath={CRM_PATHS.lists} />;
  }

  const notFound = detail.error?.graphQLErrors?.[0]?.extensions?.code === 'CRM_LIST_NOT_FOUND';
  const isList = list?.type === 'STATIC';
  const title = list?.name ?? (notFound ? 'List not found' : `List #${id}`);

  const refresh = () => {
    forgetBatchData(client.cache);
    detail.refetch();
    if (selectable) peopleQuery.refetch();
  };

  const doRecount = async () => {
    try {
      const { data } = await recount({ variables: { id } });
      toast.success(`Recounted ${data!.crmRecountList.name}: ${count(data!.crmRecountList.contactCount)} contacts.`);
      peopleQuery.refetch();
    } catch (err) {
      toast.error(describeManageError(err, 'list'));
    }
  };

  const actions = list && (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      <Tooltip title={selectable ? '' : 'This saved filter has no filter yet, so it selects nobody.'}>
        <span>
          <Button variant="contained" startIcon={<EditNoteOutlinedIcon />} onClick={() => setBatching(true)} disabled={!selectable || counts?.total === 0}>
            Batch update
          </Button>
        </span>
      </Tooltip>
      <Button
        variant="outlined"
        component={RouterLink}
        to={listPeoplePath(list.id)}
        startIcon={<OpenInNewIcon />}
        disabled={!selectable}
      >
        Open on the contact list
      </Button>
      <Button
        variant="outlined"
        component={RouterLink}
        to={queuePathFor({ ...EMPTY_STATE, listIds: [list.id] })}
        startIcon={<PhoneForwardedOutlinedIcon />}
        disabled={!selectable}
      >
        Call this {isList ? 'list' : 'filter'}
      </Button>
      <Tooltip title={selectable && list.isActive ? 'Write a campaign to everyone on it who can be emailed' : ''}>
        <span>
          <Button variant="outlined" component={RouterLink} to={emailListPath(list.id)} startIcon={<SendOutlinedIcon />} disabled={!selectable || !list.isActive}>
            Email this {isList ? 'list' : 'filter'}
          </Button>
        </span>
      </Tooltip>
    </Box>
  );

  return (
    <CrmPageFrame
      title={title}
      description={list?.description ?? (isList ? 'A list: the people added to it.' : list ? 'A saved filter: whoever it matches when used.' : undefined)}
      crumbs={[{ label: 'Lists', to: CRM_PATHS.lists }, { label: list?.name ?? `#${id}` }]}
      actions={actions}
    >
      {notFound && (
        <Alert severity="warning" action={<Button color="inherit" size="small" component={RouterLink} to={CRM_PATHS.lists}>Back to lists</Button>}>
          No list #{id}. It may have been deleted.
        </Alert>
      )}
      {detail.error && !notFound && !list && <Alert severity="error">{describeListError(detail.error)}</Alert>}
      {!list && detail.loading && <Skeleton variant="rounded" height={160} />}

      {list && (
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Item sx={{ p: { xs: 1.5, sm: 2 }, display: 'grid', gap: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
              <Chip
                size="small"
                variant="outlined"
                icon={isList ? <ListAltOutlinedIcon /> : <FilterAltOutlinedIcon />}
                label={kindLabel(list)}
                sx={{ fontWeight: 600, color: isList ? '#2e7d32' : '#6a1b9a', '& .MuiChip-icon': { color: 'inherit' } }}
              />
              {!list.isActive && <Chip size="small" color="warning" variant="outlined" label="Not re-expressed since NationBuilder" />}
              <Typography variant="caption" color="text.disabled" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                #{list.id}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Button size="small" startIcon={recounting.loading ? <CircularProgress size={14} /> : <RefreshIcon />} onClick={doRecount} disabled={recounting.loading || (!isList && !selectable)}>
                Recount
              </Button>
              <Button size="small" startIcon={<EditOutlinedIcon />} onClick={() => setEditing(true)}>
                Rename
              </Button>
              <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => setDeleting(true)}>
                Delete
              </Button>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
              <Fact label={isList ? 'On it (stored count)' : 'Matched when last counted'}>
                {count(list.contactCount)}
                {list.countedAt && (
                  <Typography variant="caption" color="text.secondary" component="span">
                    {' '}
                    · {formatDay(list.countedAt)}
                  </Typography>
                )}
              </Fact>
              <Fact label="From">{originLabel(list)}</Fact>
              <Fact label="Made">{formatDay(madeAt(list)) || '—'}</Fact>
              <Fact label="Last changed">
                {formatDay(list.updatedAt) || '—'}
                {list.updatedAt && sinceText(list.updatedAt, today) ? ` · ${sinceText(list.updatedAt, today)}` : ''}
              </Fact>
            </Box>
            {!selectable && (
              <Alert severity="warning" variant="outlined">
                This saved filter came from NationBuilder and has not been re-expressed as a filter here, so it selects nobody yet.
                NationBuilder's own criteria are kept with it.
              </Alert>
            )}
            {isList && counts && counts.total !== list.contactCount && (
              <Typography variant="caption" color="text.secondary">
                {count(list.contactCount - counts.total)} of the people on it were merged into other records since: they are counted
                on the list but not shown or changed. Their records are on the people they became.
              </Typography>
            )}
          </Item>

          {selectable && (
            <StatTiles label="Who is on it" columns={6}>
              {TILES.map((tile) => (
                <StatTile
                  key={tile.label}
                  label={tile.label}
                  value={counts?.[tile.key]}
                  hint={counts && tile.hint ? tile.hint(counts) : 'See them on the contact list'}
                  icon={tile.icon}
                  tone={tile.tone}
                  selected={false}
                  onClick={() => navigate(`${listPeoplePath(list.id)}${tile.param ? `&${tile.param}=yes` : ''}`)}
                />
              ))}
            </StatTiles>
          )}
          {peopleQuery.error && <Alert severity="error">{describeListError(peopleQuery.error)}</Alert>}

          {selectable && (
            <Item sx={{ p: { xs: 1.5, sm: 2 }, display: 'grid', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700 }}>
                  People
                </Typography>
                {counts && (
                  <Typography variant="body2" color="text.secondary">
                    {counts.total > (firstPeople?.length ?? 0) ? `the first ${firstPeople?.length ?? 0} of ${count(counts.total)}` : count(counts.total)}
                  </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                <Link component={RouterLink} to={listPeoplePath(list.id)} underline="hover" variant="body2">
                  See all on the contact list
                </Link>
              </Box>
              <Divider />
              {!firstPeople && <Skeleton variant="text" height={120} />}
              {firstPeople && firstPeople.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  {isList ? 'Nobody is on this list. Add people from the contact list: tick them, or select everyone a filter matches.' : 'Nobody matches this filter now.'}
                </Typography>
              )}
              {firstPeople && firstPeople.length > 0 && (
                <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                  {firstPeople.map((p) => (
                    <Box component="li" key={p.id} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 2fr 1fr' }, gap: 1, py: 0.75, borderBottom: '1px solid #eef1f5' }}>
                      <Link
                        component="button"
                        type="button"
                        underline="hover"
                        onClick={() => navigate(contactPath(p.id), { state: crmReturnState(location) })}
                        sx={{ textAlign: 'left', fontWeight: 600 }}
                      >
                        {p.fullName || p.primaryEmail || `Contact #${p.id}`}
                      </Link>
                      <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                        {[p.primaryEmail, p.primaryPhone].filter(Boolean).join(' · ') || '—'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {[p.city, p.countryCode].filter(Boolean).join(', ') || '—'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Item>
          )}

          <Item sx={{ p: { xs: 1.5, sm: 2 }, display: 'grid', gap: 1 }}>
            <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700 }}>
              Batch updates on this {isList ? 'list' : 'saved filter'}
            </Typography>
            <BatchHistory segmentId={list.id} take={10} emptyText="None yet. Batch update changes everyone on it at once, and can be undone." onUndone={refresh} />
          </Item>
        </Box>
      )}

      {batching && list && (
        <BatchUpdateDialog
          filter={filter}
          who={`Everyone ${isList ? 'on' : 'matched by'} ${list.name}`}
          segmentId={list.id}
          onClose={(changed) => {
            setBatching(false);
            if (changed) refresh();
          }}
        />
      )}
      {editing && list && (
        <ListEditorDialog
          list={list}
          onClose={() => setEditing(false)}
          onSaved={(saved) => {
            setEditing(false);
            toast.success(`Saved ${saved.name}.`);
            refresh();
          }}
        />
      )}
      {deleting && list && (
        <DeleteListDialog
          list={list}
          onClose={() => setDeleting(false)}
          onDeleted={() => {
            setDeleting(false);
            forgetBatchData(client.cache);
            toast.success(`Deleted ${list.name}.`);
            navigate(CRM_PATHS.lists);
          }}
        />
      )}
    </CrmPageFrame>
  );
};
